import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Package, MapPin, User as UserIcon, ChevronDown, Truck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, chf, fileUrl, imgSrc, fontLabel } from "../lib/api";
import SEO from "../components/SEO";

const inputCls = "w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink";

function AuthForms() {
  const { customerLogin, customerRegister } = useApp();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "login") await customerLogin(f.email.trim(), f.password);
      else await customerRegister(f.name.trim(), f.email.trim(), f.password);
      toast.success(mode === "login" ? "Connecté" : "Compte créé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md">
      <h1 className="font-serif text-4xl font-light tracking-tight text-ink">Mon compte</h1>
      <p className="mt-2 text-ink2">Connectez-vous pour sauvegarder votre panier et suivre vos commandes.</p>

      <button onClick={googleLogin} data-testid="google-login-btn"
        className="mt-8 flex w-full items-center justify-center gap-3 border border-ink bg-cream py-3 text-sm font-medium text-ink transition-colors hover:bg-sand">
        <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        Continuer avec Google
      </button>

      <div className="my-6 flex items-center gap-4 text-xs uppercase tracking-widest text-ink3">
        <span className="h-px flex-1 bg-line" /> ou <span className="h-px flex-1 bg-line" />
      </div>

      <div className="mb-4 flex gap-2">
        <button onClick={() => setMode("login")} data-testid="tab-login"
          className={`flex-1 border py-2 text-xs uppercase tracking-widest ${mode === "login" ? "border-ink bg-ink text-cream" : "border-line text-ink2"}`}>Connexion</button>
        <button onClick={() => setMode("register")} data-testid="tab-register"
          className={`flex-1 border py-2 text-xs uppercase tracking-widest ${mode === "register" ? "border-ink bg-ink text-cream" : "border-line text-ink2"}`}>Créer un compte</button>
      </div>

      <div className="space-y-3">
        {mode === "register" && (
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nom et prénom" data-testid="account-name" className={inputCls} />
        )}
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} type="email" placeholder="E-mail" data-testid="account-email" className={inputCls} />
        <input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} type="password" placeholder="Mot de passe (min. 8 caractères)" data-testid="account-password" className={inputCls} />
        <button onClick={submit} disabled={loading} data-testid="account-submit"
          className="w-full bg-ink py-3 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra disabled:opacity-50">
          {loading ? "..." : mode === "login" ? "Se connecter" : "Créer mon compte"}
        </button>
      </div>
    </div>
  );
}

function OrderCard({ o }) {
  const [open, setOpen] = useState(false);
  const addr = o.shipping_address || null;
  return (
    <li className="py-1" data-testid={`order-${o.id}`}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`order-toggle-${o.id}`}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm transition-colors hover:bg-sand/60 px-2 -mx-2"
      >
        <span className="flex items-center gap-2 text-ink2">
          <ChevronDown className={`h-4 w-4 shrink-0 text-terra transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          {(o.created_at || "").slice(0, 10)} · {(o.items || []).length} article(s)
        </span>
        <span className="font-medium text-ink">{chf(o.total)}{o.shipped ? " · Expédiée" : " · En préparation"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="border border-line bg-white/60 p-4 md:p-5" data-testid={`order-details-${o.id}`}>
              {/* Articles */}
              <ul className="divide-y divide-line" data-testid={`order-items-${o.id}`}>
                {(o.items || []).map((it, idx) => {
                  const cust = it.customization || {};
                  const sides = cust.sides || null;
                  return (
                    <li key={idx} className="flex gap-4 py-3 first:pt-0 last:pb-0">
                      <img src={imgSrc(it.image_url)} alt="" className="h-20 w-16 shrink-0 border border-line bg-white object-contain" />
                      <div className="flex-1 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <p className="font-medium text-ink">{it.name} <span className="text-ink3">×{it.quantity}</span></p>
                          <p className="shrink-0 text-ink">{chf((it.unit_price || 0) * (it.quantity || 1))}</p>
                        </div>
                        <p className="text-ink3">{it.category}{it.technique ? ` · ${it.technique}` : ""} · {chf(it.unit_price)} / unité</p>
                        {it.size && <p className="mt-1 text-ink2">{it.size_label || "Taille"} : <strong>{it.size}</strong></p>}
                        {sides ? (
                          Object.entries(sides).map(([face, c]) => (
                            <div key={face} className="mt-1 border-l-2 border-terra/40 pl-2">
                              <p className="text-[11px] uppercase tracking-wider text-terra">{face}</p>
                              {c.text?.value && <p className="text-ink2">Texte : « {c.text.value} »</p>}
                              {c.text?.font && <p className="text-ink2">Police : {fontLabel(c.text.font)}</p>}
                              {c.image?.path && <p className="text-ink2">Votre image ({face})</p>}
                            </div>
                          ))
                        ) : (cust.text || cust.image_path) ? (
                          <div className="mt-1 border-l-2 border-terra/40 pl-2">
                            {cust.text && <p className="text-ink2">Texte : « {cust.text} »</p>}
                            {cust.font && <p className="text-ink2">Police : {fontLabel(cust.font)}</p>}
                            {cust.image_path && <p className="text-ink2">Avec votre image</p>}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-4 grid gap-4 border-t border-line pt-4 md:grid-cols-2">
                {/* Adresse */}
                {addr && (
                  <div data-testid={`order-address-${o.id}`}>
                    <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-ink3"><Truck className="h-3.5 w-3.5 text-terra" /> Livraison</p>
                    <p className="text-sm text-ink2">
                      {addr.full_name}<br />
                      {addr.line1}<br />
                      {addr.postal_code} {addr.city}<br />
                      {addr.country}
                    </p>
                  </div>
                )}
                {/* Totaux */}
                <div className="text-sm" data-testid={`order-totals-${o.id}`}>
                  <p className="mb-1 text-[11px] uppercase tracking-widest text-ink3">Récapitulatif</p>
                  <div className="space-y-1 text-ink2">
                    <p className="flex justify-between"><span>Sous-total</span><span>{chf(o.subtotal ?? o.total)}</span></p>
                    {o.discount_percent > 0 && (
                      <p className="flex justify-between text-sage"><span>Réduction (−{o.discount_percent}%)</span><span>−{chf((o.subtotal ?? 0) * o.discount_percent / 100)}</span></p>
                    )}
                    <p className="flex justify-between"><span>Frais de livraison</span><span>{o.shipping > 0 ? chf(o.shipping) : "Offerts"}</span></p>
                    <p className="flex justify-between border-t border-line pt-1 font-medium text-ink"><span>Total</span><span>{chf(o.total)}</span></p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

function Dashboard() {
  const { customer, setCustomer, customerLogout } = useApp();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ name: customer.name || "", email: customer.email || "", password: "" });
  const [addr, setAddr] = useState(customer.shipping_address || { full_name: "", line1: "", postal_code: "", city: "", country: "Suisse" });

  useEffect(() => {
    api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/account/profile", { name: profile.name, email: profile.email, password: profile.password || undefined });
      setCustomer(data); setProfile({ ...profile, password: "" });
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveAddr = async () => {
    try {
      const { data } = await api.put("/account/address", addr);
      setCustomer({ ...customer, shipping_address: data.shipping_address });
      toast.success("Adresse enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="account-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-4xl font-light tracking-tight text-ink">Bonjour {customer.name || ""}</h1>
          <p className="mt-1 text-ink2">{customer.email}</p>
        </div>
        <button onClick={customerLogout} data-testid="account-logout" className="flex items-center gap-2 border border-line px-4 py-2 text-xs uppercase tracking-widest text-ink2 hover:bg-sand">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>

      <div className="mt-10 grid gap-8 md:grid-cols-2">
        {/* Profil */}
        <div className="border border-line bg-cream p-6">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl font-medium text-ink"><UserIcon className="h-5 w-5 text-terra" /> Profil</h2>
          <div className="space-y-3">
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Nom" data-testid="profile-name" className={inputCls} />
            <input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} type="email" placeholder="E-mail" data-testid="profile-email" className={inputCls} />
            <input value={profile.password} onChange={(e) => setProfile({ ...profile, password: e.target.value })} type="password" placeholder="Nouveau mot de passe (optionnel)" data-testid="profile-password" className={inputCls} />
            <button onClick={saveProfile} data-testid="profile-save" className="w-full bg-ink py-2.5 text-xs uppercase tracking-widest text-cream hover:bg-terra">Enregistrer</button>
          </div>
        </div>

        {/* Adresse */}
        <div className="border border-line bg-cream p-6">
          <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl font-medium text-ink"><MapPin className="h-5 w-5 text-terra" /> Adresse de livraison</h2>
          <div className="space-y-3">
            <input value={addr.full_name} onChange={(e) => setAddr({ ...addr, full_name: e.target.value })} placeholder="Nom et prénom" data-testid="acc-addr-name" className={inputCls} />
            <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Rue et numéro" data-testid="acc-addr-line1" className={inputCls} />
            <div className="flex gap-2">
              <input value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} placeholder="NPA" className={`${inputCls} w-1/3`} data-testid="acc-addr-postal" />
              <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="Ville" className={`${inputCls} w-2/3`} data-testid="acc-addr-city" />
            </div>
            <input value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} placeholder="Pays" data-testid="acc-addr-country" className={inputCls} />
            <button onClick={saveAddr} data-testid="address-save" className="w-full bg-ink py-2.5 text-xs uppercase tracking-widest text-cream hover:bg-terra">Enregistrer l'adresse</button>
          </div>
        </div>
      </div>

      {/* Commandes */}
      <div className="mt-8 border border-line bg-cream p-6">
        <h2 className="mb-4 flex items-center gap-2 font-serif text-2xl font-medium text-ink"><Package className="h-5 w-5 text-terra" /> Mes commandes</h2>
        {orders.length === 0 ? (
          <p className="text-ink3">Aucune commande pour le moment. <Link to="/catalogue" className="link-underline text-ink">Voir le catalogue</Link></p>
        ) : (
          <ul className="divide-y divide-line" data-testid="orders-list">
            {orders.map((o) => <OrderCard key={o.id} o={o} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function Account() {
  const { customer, setCustomer, completeGoogleLogin } = useApp();
  const processed = useRef(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sid = new URLSearchParams(hash.slice(1)).get("session_id");
      completeGoogleLogin(sid)
        .then(() => {
          window.history.replaceState(null, "", "/compte");
          toast.success("Connecté avec Google");
        })
        .catch(() => { toast.error("Connexion Google échouée"); window.history.replaceState(null, "", "/compte"); });
    }
  }, [completeGoogleLogin]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 pt-28 pb-28 md:px-10">
      <SEO title="Mon compte" path="/compte" noindex />
      {customer && customer.user_id ? <Dashboard /> : <AuthForms />}
    </div>
  );
}
